import React, { useEffect, useMemo, useRef, useState } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import SafeIcon from '../components/common/SafeIcon';
import { useToast } from '../components/ui/Toast';
import * as FiIcons from 'react-icons/fi';

const { FiArchive, FiCheck, FiEdit3, FiPlus, FiRefreshCw } = FiIcons;
const STORAGE_KEY = 'habitGarden.v3';
const LEGACY_STORAGE_KEY = 'habitGarden.v2';
const DAYS = [
  ['Sun', 0], ['Mon', 1], ['Tue', 2], ['Wed', 3],
  ['Thu', 4], ['Fri', 5], ['Sat', 6],
];

const localDate = (date = new Date()) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const parseDate = (value) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const shiftDate = (value, amount) => {
  const date = parseDate(value);
  date.setDate(date.getDate() + amount);
  return localDate(date);
};

const normalizeHabit = (habit, index) => ({
  ...habit,
  id: String(habit.id ?? `legacy-${index}`),
  name: String(habit.name || habit.title || '').trim(),
  minimumAction: String(habit.minimumAction || '').trim(),
  recurrenceWeekdays: Array.isArray(habit.recurrenceWeekdays)
    ? [...new Set(habit.recurrenceWeekdays.map(Number))]
    : [0, 1, 2, 3, 4, 5, 6],
  completionHistory: Array.isArray(habit.completionHistory)
    ? [...new Set(habit.completionHistory.filter(Boolean))]
    : [],
  archived: Boolean(habit.archived),
  streak: Number(habit.streak) || 0,
  lastDoneISO: habit.lastDoneISO || null,
  createdAt: habit.createdAt || Date.now(),
});

const toLocalDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = parseDate(value);
    return Number.isNaN(parsed.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : localDate(parsed);
};

const enhanceHabit = (habit) => {
  const knownLastDone = toLocalDate(habit.lastDoneISO);
  return {
    ...habit,
    completionHistory: habit.completionHistory.length || !knownLastDone
      ? habit.completionHistory
      : [knownLastDone],
    checkInUndo: habit.checkInUndo && typeof habit.checkInUndo === 'object'
      ? habit.checkInUndo
      : {},
  };
};

const isScheduledOn = (habit, date) => {
  const created = toLocalDate(habit.createdAt);
  return (!created || date >= created)
    && habit.recurrenceWeekdays.includes(parseDate(date).getDay());
};

function Plant({ count }) {
  return (
    <svg viewBox="0 0 120 90" role="img" aria-label={`${count} known completions this week`} className="h-24 w-full">
      <path d="M12 78 Q60 66 108 78" fill="none" stroke="#D8CBB7" strokeWidth="5" strokeLinecap="round" />
      <path d="M60 76 C59 58 61 40 60 20" fill="none" stroke="#52745D" strokeWidth="4" strokeLinecap="round" opacity={count ? 1 : 0.25} />
      {count >= 1 && <path d="M60 62 C48 57 42 50 40 42 C51 42 58 48 60 56Z" fill="#9BB898" />}
      {count >= 2 && <path d="M60 54 C72 50 78 43 80 35 C69 36 62 42 60 48Z" fill="#6F9478" />}
      {count >= 3 && <path d="M60 43 C49 38 46 31 46 25 C55 27 60 32 61 37Z" fill="#B7CAA8" />}
      {count >= 4 && <path d="M61 36 C72 31 76 25 76 18 C67 20 62 25 60 30Z" fill="#83A98C" />}
      {count >= 5 && <circle cx="60" cy="18" r="9" fill="#E8B96A" />}
      {count >= 6 && <circle cx="51" cy="18" r="6" fill="#F0C98B" />}
      {count >= 7 && <circle cx="69" cy="18" r="6" fill="#F0C98B" />}
    </svg>
  );
}

function HabitForm({ habit, onSave, onCancel }) {
  const titleRef = useRef(null);
  const minimumRef = useRef(null);
  const [days, setDays] = useState(habit?.recurrenceWeekdays || [0, 1, 2, 3, 4, 5, 6]);

  const submit = (event) => {
    event.preventDefault();
    const name = titleRef.current?.value.trim();
    if (!name || !days.length) return;

    onSave({
      name,
      minimumAction: minimumRef.current?.value.trim() || '',
      recurrenceWeekdays: [...days].sort(),
    });
  };

  return (
    <form onSubmit={submit} className="p-6 space-y-5">
      <div>
        <label htmlFor="habit-title" className="block mb-2 text-sm font-bold text-pine">
          Habit title
        </label>
        <input
          id="habit-title"
          ref={titleRef}
          required
          autoFocus
          maxLength={80}
          defaultValue={habit?.name || ''}
          placeholder="Read before bed"
          className="w-full rounded-2xl border border-pine/15 bg-paper px-4 py-3 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
        />
      </div>

      <div>
        <label htmlFor="habit-minimum" className="block mb-2 text-sm font-bold text-pine">
          Minimum action <span className="font-normal text-pine/50">(optional)</span>
        </label>
        <input
          id="habit-minimum"
          ref={minimumRef}
          maxLength={100}
          defaultValue={habit?.minimumAction || ''}
          placeholder="Read one page"
          className="w-full rounded-2xl border border-pine/15 bg-paper px-4 py-3 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
        />
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-bold text-pine">Repeat on</legend>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {DAYS.map(([label, day]) => {
            const selected = days.includes(day);
            return (
              <button
                key={day}
                type="button"
                aria-pressed={selected}
                onClick={() => setDays((current) => (
                  selected ? current.filter((item) => item !== day) : [...current, day]
                ))}
                className={`rounded-xl px-2 py-2 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-sage ${
                  selected ? 'bg-pine text-paper' : 'border border-pine/15 bg-white text-pine'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {!days.length && <p role="alert" className="mt-2 text-xs text-crimson">Choose at least one day.</p>}
      </fieldset>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={!days.length}>{habit ? 'Save habit' : 'Add habit'}</Button>
      </div>
    </form>
  );
}

export default function HabitGarden() {
  const { success, error } = useToast();
  const [today, setToday] = useState(() => localDate());
  const [view, setView] = useState('due');
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [state, setState] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      const legacy = parsed ? null : JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || 'null');
      const source = parsed || legacy;
      return {
        ...(source && typeof source === 'object' ? source : {}),
        habits: Array.isArray(source?.habits)
          ? source.habits.map(normalizeHabit).map(enhanceHabit).filter((habit) => habit.name)
          : [],
        filter: source?.filter || 'all',
        mantraIndex: source?.mantraIndex ?? 0,
      };
    } catch (readError) {
      console.error('Failed to parse habit garden data:', readError);
      return { habits: [], filter: 'all', mantraIndex: 0 };
    }
  });

  useEffect(() => {
    const timer = window.setInterval(() => setToday(localDate()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (saveError) {
      console.error('Failed to save habit garden data:', saveError);
      error('Habit changes could not be saved on this device.');
    }
  }, [error, state]);

  const weekday = parseDate(today).getDay();
  const week = useMemo(
    () => Array.from({ length: 7 }, (_, index) => shiftDate(today, index - 6)),
    [today]
  );
  const active = state.habits.filter((habit) => !habit.archived);
  const archived = state.habits.filter((habit) => habit.archived);
  const due = active.filter((habit) => habit.recurrenceWeekdays.includes(weekday));
  const dueSinceCreation = due.filter((habit) => isScheduledOn(habit, today));
  const visible = view === 'due' ? dueSinceCreation : active;
  const todayDone = dueSinceCreation.filter((habit) => habit.completionHistory.includes(today)).length;
  const knownCompletions = active.reduce(
    (total, habit) => total + habit.completionHistory.filter((date) => week.includes(date)).length,
    0
  );
  const opportunities = active.reduce(
    (total, habit) => total + week.filter((date) => (
      habit.recurrenceWeekdays.includes(parseDate(date).getDay())
      && isScheduledOn(habit, date)
    )).length,
    0
  );
  const consistency = opportunities ? Math.round((knownCompletions / opportunities) * 100) : 0;

  const saveHabit = (values) => {
    setState((current) => ({
      ...current,
      habits: editing
        ? current.habits.map((habit) => habit.id === editing.id ? { ...habit, ...values } : habit)
        : [...current.habits, enhanceHabit(normalizeHabit({
          ...values,
          id: `habit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          completionHistory: [],
          streak: 0,
          lastDoneISO: null,
          archived: false,
          createdAt: Date.now(),
        }, current.habits.length))],
    }));
    success(editing ? 'Habit updated.' : 'Habit added.');
    setEditing(null);
    setFormOpen(false);
  };

  const toggleToday = (habitId) => {
    const habit = state.habits.find((item) => item.id === habitId);
    if (!habit) return;
    const done = habit.completionHistory.includes(today);

    setState((current) => ({
      ...current,
      habits: current.habits.map((item) => {
        if (item.id !== habitId) return item;
        if (!done) {
          return {
            ...item,
            completionHistory: [...item.completionHistory, today],
            lastDoneISO: today,
            streak: item.streak + 1,
            checkInUndo: {
              ...item.checkInUndo,
              [today]: {
                streak: item.streak,
                lastDoneISO: item.lastDoneISO,
              },
            },
          };
        }

        const history = item.completionHistory.filter((date) => date !== today);
        const previous = item.checkInUndo?.[today];
        const checkInUndo = { ...item.checkInUndo };
        delete checkInUndo[today];
        return {
          ...item,
          completionHistory: history,
          lastDoneISO: previous ? previous.lastDoneISO : (
            [...history].sort().at(-1) || (
              item.lastDoneISO !== today ? item.lastDoneISO : null
            )
          ),
          streak: previous ? previous.streak : Math.max(0, item.streak - 1),
          checkInUndo,
        };
      }),
    }));
    success(done ? 'Today’s check-in was undone.' : 'Today is recorded.');
  };

  const archiveHabit = (habitId, archivedValue) => {
    setState((current) => ({
      ...current,
      habits: current.habits.map((habit) => (
        habit.id === habitId ? { ...habit, archived: archivedValue } : habit
      )),
    }));
    success(archivedValue ? 'Habit archived.' : 'Habit restored.');
  };

  return (
    <div className="max-w-6xl mx-auto space-y-7">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-5">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sage">
            Your effort, made visible.
          </p>
          <h1 className="text-4xl lg:text-5xl font-cormorant font-bold text-pine">
            Small steps, visible growth.
          </h1>
          <p className="mt-2 text-pine/65">
            Keep the actions that support you close and easy to repeat.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} icon={FiPlus}>
          Add habit
        </Button>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <Card className="p-4 bg-sage/15">
          <strong className="block text-2xl text-pine">{todayDone}/{dueSinceCreation.length}</strong>
          <span className="text-xs text-pine/60">due today checked in</span>
        </Card>
        <Card className="p-4 bg-apricot/15">
          <strong className="block text-2xl text-pine">{consistency}%</strong>
          <span className="text-xs text-pine/60">known weekly consistency</span>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-2xl border border-pine/10 bg-white p-1">
          {[['due', 'Due today'], ['all', 'All habits']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => setView(value)}
              className={`rounded-xl px-4 py-2 text-sm font-bold ${
                view === value ? 'bg-pine text-paper' : 'text-pine/65'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {!!archived.length && (
          <button
            type="button"
            aria-expanded={showArchived}
            onClick={() => setShowArchived((current) => !current)}
            className="text-sm font-bold text-pine/60 hover:text-pine"
          >
            {showArchived ? 'Hide archived' : `Archived (${archived.length})`}
          </button>
        )}
      </div>

      {visible.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((habit) => {
            const done = habit.completionHistory.includes(today);
            const weekCount = habit.completionHistory.filter((date) => week.includes(date)).length;
            const historyOpen = expanded === habit.id;

            return (
              <Card key={habit.id} className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-pine">{habit.name}</h2>
                    {habit.minimumAction && (
                      <p className="mt-1 text-sm text-pine/60">Minimum: {habit.minimumAction}</p>
                    )}
                    <p className="mt-2 text-xs text-pine/45">
                      {habit.recurrenceWeekdays.length === 7
                        ? 'Every day'
                        : habit.recurrenceWeekdays.map((day) => DAYS.find((item) => item[1] === day)?.[0]).join(', ')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setEditing(habit); setFormOpen(true); }}
                    aria-label={`Edit ${habit.name}`}
                    className="rounded-xl p-2 text-pine/50 hover:bg-pine/5 focus:outline-none focus:ring-2 focus:ring-sage"
                  >
                    <SafeIcon icon={FiEdit3} />
                  </button>
                </div>

                <button
                  type="button"
                  aria-expanded={historyOpen}
                  onClick={() => setExpanded(historyOpen ? null : habit.id)}
                  className="mt-3 w-full rounded-2xl bg-paper px-4 pt-2 focus:outline-none focus:ring-2 focus:ring-sage"
                >
                  <Plant count={weekCount} />
                </button>

                {historyOpen && (
                  <div className="mt-3 grid grid-cols-7 gap-1" aria-label="Known seven-day completion history">
                    {week.map((date) => {
                      const known = habit.completionHistory.includes(date);
                      const beforeCreation = Boolean(
                        toLocalDate(habit.createdAt) && date < toLocalDate(habit.createdAt)
                      );
                      return (
                        <div key={date} className="text-center">
                          <span
                            aria-label={`${date}: ${known ? 'completed' : 'no recorded completion'}`}
                            title={beforeCreation ? 'Not scheduled yet' : undefined}
                            className={`mx-auto flex h-7 w-7 items-center justify-center rounded-full ${
                              known ? 'bg-sage text-pine' : 'border border-pine/10 text-pine/30'
                            }`}
                          >
                            {known ? <SafeIcon icon={FiCheck} className="h-3 w-3" /> : '·'}
                          </span>
                          <span className="mt-1 block text-[10px] text-pine/45">
                            {DAYS[parseDate(date).getDay()][0].slice(0, 1)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!habit.completionHistory.length && habit.streak > 0 && (
                  <p className="mt-3 text-xs text-pine/50">
                    {habit.streak} earlier completion{habit.streak === 1 ? '' : 's'} preserved;
                    exact dates were not recorded.
                  </p>
                )}

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-pine/10 pt-4">
                  <button
                    type="button"
                    onClick={() => archiveHabit(habit.id, true)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-pine/50 hover:text-pine"
                  >
                    <SafeIcon icon={FiArchive} /> Archive
                  </button>
                  <Button
                    size="sm"
                    variant={done ? 'secondary' : 'primary'}
                    icon={done ? FiRefreshCw : FiCheck}
                    onClick={() => toggleToday(habit.id)}
                  >
                    {done ? 'Undo today' : 'Check in'}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-10 text-center border-dashed">
          <h2 className="text-xl font-cormorant font-bold text-pine">
            {active.length ? 'Nothing is due today.' : 'Begin with one small step.'}
          </h2>
          <p className="mt-1 text-sm text-pine/60">
            {active.length
              ? 'Your other habits remain available under All habits.'
              : 'Add a habit and choose the days that suit it.'}
          </p>
        </Card>
      )}

      {showArchived && (
        <section aria-labelledby="archived-title">
          <h2 id="archived-title" className="mb-3 text-xl font-cormorant font-bold text-pine">
            Archived habits
          </h2>
          <div className="space-y-2">
            {archived.map((habit) => (
              <Card key={habit.id} className="flex items-center justify-between gap-3 p-4">
                <span className="font-bold text-pine">{habit.name}</span>
                <Button size="sm" variant="secondary" onClick={() => archiveHabit(habit.id, false)}>
                  Restore
                </Button>
              </Card>
            ))}
          </div>
        </section>
      )}

      <Modal
        isOpen={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        title={editing ? 'Edit habit' : 'Add a habit'}
      >
        <HabitForm
          key={editing?.id || 'new'}
          habit={editing}
          onSave={saveHabit}
          onCancel={() => { setFormOpen(false); setEditing(null); }}
        />
      </Modal>
    </div>
  );
}